import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Truck, Save, Shirt, AlertCircle, LogIn, Upload, Check } from 'lucide-react';

const methods = [
  { key: 'pickup_measurement', label: 'Pickup Measurement', desc: 'We visit you for accurate measurements', icon: Truck },
  { key: 'saved_measurement', label: 'Saved Measurement', desc: 'Use a previously saved measurement profile', icon: Save },
  { key: 'reference_garment', label: 'Reference Garment', desc: 'Send us a garment for reference', icon: Shirt },
];

export default function MeasurementStep({ measurementState, onChange, categorySlug, onCanProceed }) {
  const { isAuthenticated } = useAuth();
  const [savedMeasurements, setSavedMeasurements] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated && measurementState.method === 'saved_measurement') {
      setLoadingSaved(true);
      api.get('/measurements').then((res) => {
        const all = res.data.data || [];
        const filtered = categorySlug ? all.filter(m => m.category_slug === categorySlug) : all;
        setSavedMeasurements(filtered);
      }).catch(() => {}).finally(() => setLoadingSaved(false));
    }
  }, [isAuthenticated, measurementState.method, categorySlug]);

  // Notify parent whether user can proceed to review
  useEffect(() => {
    if (!onCanProceed) return;
    if (measurementState.method === 'saved_measurement') {
      // Can only proceed if logged in AND a profile is selected
      const canProceed = isAuthenticated && !!measurementState.profile_id;
      onCanProceed(canProceed);
    } else if (measurementState.method) {
      onCanProceed(true);
    } else {
      onCanProceed(false);
    }
  }, [measurementState, isAuthenticated, onCanProceed]);

  const handleRefGarmentUpload = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await api.post('/upload/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data.data?.url || res.data.data?.image_url;
      if (url) {
        onChange({ ...measurementState, reference_image: url });
      } else {
        toast.error('Upload succeeded but no URL was returned. Please try again.');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Upload failed';
      // eslint-disable-next-line no-console
      console.error('[upload/image] failed:', err?.response?.status, err?.response?.data || err);
      toast.error(`Upload failed: ${msg}`);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div data-testid="measurement-step">
      <div className="mb-8">
        <div className="uppercase tracking-luxe text-[11px] font-semibold text-gold mb-3">Step 03 of 03</div>
        <h2 className="font-display font-light text-ink leading-none tracking-[-0.015em]" style={{ fontSize: 'clamp(30px, 4vw, 44px)' }}>How would you like us to measure?</h2>
      </div>
      <div className="space-y-4">
        {methods.map((m) => {
          const isSelected = measurementState.method === m.key;
          return (
            <div key={m.key}>
              <button
                onClick={() => onChange({ ...measurementState, method: m.key, profile_id: undefined, profile_name: undefined, reference_image: m.key !== 'reference_garment' ? undefined : measurementState.reference_image })}
                className={`w-full flex items-center gap-5 p-5 text-left transition-all ${
                  isSelected ? 'border border-ink outline outline-1 outline-offset-2 outline-ink' : 'border border-border hover:border-ink/40'
                }`}
                data-testid={`measurement-${m.key}`}
              >
                <div className={`w-14 h-14 flex-shrink-0 flex items-center justify-center border ${isSelected ? 'border-ink bg-ink text-background' : 'border-border text-ink-muted'}`}>
                  <m.icon className="w-5 h-5" strokeWidth={1.2} />
                </div>
                <div>
                  <p className="font-display text-ink" style={{ fontSize: 22 }}>{m.label}</p>
                  <p className="text-[13px] text-ink/70 mt-1">{m.desc}</p>
                </div>
              </button>

              {/* Saved measurements list */}
              {isSelected && m.key === 'saved_measurement' && (
                <div className="ml-5 mt-4 space-y-2.5" data-testid="saved-measurements-list">
                  {!isAuthenticated ? (
                    <div className="flex items-center justify-between p-4 bg-secondary text-[13px] text-ink border border-border">
                      <span className="flex items-center gap-2"><AlertCircle className="w-4 h-4 flex-shrink-0 text-gold" /> Please sign in to view saved measurements.</span>
                      <Link to="/login" onClick={() => sessionStorage.setItem('besq_return', window.location.pathname + window.location.search)} className="inline-flex items-center gap-1.5 border border-ink bg-ink text-background hover:bg-transparent hover:text-ink px-4 py-2 uppercase tracking-luxe text-[10px] font-semibold transition-all" data-testid="measurement-login-btn"><LogIn className="w-3.5 h-3.5" /> Sign In</Link>
                    </div>
                  ) : loadingSaved ? (
                    <div className="p-4 bg-secondary text-[13px] text-ink-muted animate-pulse uppercase tracking-luxe font-semibold">Loading…</div>
                  ) : savedMeasurements.length === 0 ? (
                    <div className="space-y-2">
                      <div className="flex items-start gap-2 p-4 border border-gold/40 bg-gold/5 text-[13px] text-ink" data-testid="no-saved-measurements">
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-gold" />
                        No saved measurements for this category yet. We'll add them after your first order is confirmed.
                      </div>
                      <p className="text-[12px] text-ink-muted ml-1">Please choose <strong className="text-ink">Pickup Measurement</strong> or <strong className="text-ink">Reference Garment</strong> instead.</p>
                    </div>
                  ) : (
                    savedMeasurements.map((sm) => (
                      <button
                        key={sm.id}
                        onClick={() => onChange({ ...measurementState, method: 'saved_measurement', profile_id: sm.id, profile_name: sm.name, ...(sm.measurements || {}) })}
                        className={`w-full flex items-center justify-between p-4 border text-left transition-all ${
                          measurementState.profile_id === sm.id ? 'border-ink bg-secondary/50' : 'border-border hover:border-ink/40'
                        }`}
                        data-testid={`saved-measurement-${sm.id}`}
                      >
                        <span className="font-display text-ink" style={{ fontSize: 18 }}>{sm.name}</span>
                        {measurementState.profile_id === sm.id && <span className="uppercase tracking-luxe text-[10px] font-semibold text-gold">✓ Selected</span>}
                      </button>
                    ))
                  )}
                </div>
              )}

              {/* Reference garment image upload */}
              {isSelected && m.key === 'reference_garment' && (
                <div className="ml-5 mt-4" data-testid="reference-garment-upload">
                  <p className="uppercase tracking-luxe text-[11px] font-semibold text-ink-muted mb-3">Upload a photo of your reference garment (optional)</p>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => fileRef.current?.click()}
                      className={`flex items-center gap-2 px-5 py-3 border transition-all uppercase tracking-luxe text-[11px] font-semibold ${
                        measurementState.reference_image
                          ? 'border-ink bg-secondary text-ink'
                          : 'border-dashed border-ink/40 hover:border-ink text-ink'
                      }`}
                      disabled={uploading}
                      data-testid="upload-reference-btn"
                    >
                      {measurementState.reference_image ? (
                        <><Check className="w-4 h-4" /> Image Uploaded</>
                      ) : (
                        <><Upload className="w-4 h-4" /> {uploading ? 'Uploading…' : 'Upload Photo'}</>
                      )}
                    </button>
                    {measurementState.reference_image && (
                      <div className="w-16 h-16 border border-border overflow-hidden">
                        <img
                          src={measurementState.reference_image.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${measurementState.reference_image}` : measurementState.reference_image}
                          alt="Reference"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleRefGarmentUpload(e.target.files[0])} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
